import React, { Dispatch, FocusEvent, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { isMobile, isSafari } from 'react-device-detect';
import { KeyboardLayouts } from '../../controls/SimpleKeyboard/Layout';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import {
    GetShiftLayoutProps,
    GetValueReturn,
    KeyboardActions,
    KeyboardDispatchTypes,
    KeyboardFormFieldFunction,
    KeyboardProviderProps,
    KeyboardState,
    OnInputChangeProps,
    OnInputChangeReturn,
    SetValueProps,
    UseKeyboardIntegrationProps,
    UseKeyboardIntegrationReturn,
} from './keyboard.types';
import { GoMotoFunction } from '../../API/GoMoto.types';

const focusedClass = 'inputFocused';
const keyboardOpenclass = 'kioskKeyboardOpen';

const keyboardReducer = (state: KeyboardState, action: KeyboardActions): KeyboardState => {
    switch (action.type) {
        case KeyboardDispatchTypes.OnFieldFocused:
            return {
                ...state,
                inputFocused: true,
                keyboardHidden: false,
                inputElement: action.payload.inputElement,
                keyboardLayout: action.payload.keyboardLayout,
                shiftAfterPeriod: action.payload.shiftAfterPeriod,
                shiftAfterSpace: action.payload.shiftAfterSpace,
                shiftAfterAmpersand: action.payload.shiftAfterAmpersand,
                hideKeyboardOnClickOut: action.payload.hideKeyboardOnClickOut,
                capsLock: action.payload.capsLock,
                getValue: action.payload.getValue,
                setValue: action.payload.setValue,
                onInputChange: action.payload.onInputChange,
                onKeyboardInput: action.payload.onKeyboardInput,
            };
        case KeyboardDispatchTypes.OnFieldRerender:
            return {
                ...state,
                getValue: action.payload.getValue,
                setValue: action.payload.setValue,
                onInputChange: action.payload.onInputChange,
                onKeyboardInput: action.payload.onKeyboardInput,
            };
        case KeyboardDispatchTypes.OnFieldBlurred:
            return {
                ...state,
                inputFocused: false,
            };
        case KeyboardDispatchTypes.OnKeyboardInput:
            return {
                ...state,
                inputFocused: true,
            };
        case KeyboardDispatchTypes.KeyboardLayoutChanged:
            return {
                ...state,
                keyboardLayout: action.payload,
            };
        case KeyboardDispatchTypes.KeyboardOpened:
            return {
                ...state,
                keyboardOpen: true,
            };
        case KeyboardDispatchTypes.KeyboardClosed:
            return {
                ...state,
                keyboardOpen: false,
                keyboardLayout: KeyboardLayouts.Alphanumeric,
                keyboardHidden: true,
                shiftAfterPeriod: false,
                shiftAfterSpace: false,
                shiftAfterAmpersand: false,
                hideKeyboardOnClickOut: false,
                capsLock: false,
            };
        case KeyboardDispatchTypes.KeyboardHidden:
            return {
                ...state,
                keyboardHidden: action.payload,
            };
        case KeyboardDispatchTypes.CapsLockChanged:
            return {
                ...state,
                capsLock: action.payload.capslock,
                keyboardLayout: action.payload.keyboardLayout,
            };
        case KeyboardDispatchTypes.ClickOutOfKeyboard:
            // do not move the condition into the brackets, this would effect logic
            return state.hideKeyboardOnClickOut ? {
                ...state,
                keyboardHidden: !state.inputFocused,
            } : {
                ...state,
            };
        default:
            return state;
    }
};

const initialState: KeyboardState = {
    inputFocused: false,
    inputElement: undefined,
    keyboardLayout: KeyboardLayouts.Alphanumeric,
    keyboardOpen: false,
    keyboardHidden: true,
    capsLock: false,
    shiftAfterPeriod: false,
    shiftAfterSpace: false,
    shiftAfterAmpersand: false,
    hideKeyboardOnClickOut: true,
    getValue: () => ({ inputValue: '',
        caretPositionStart: 0,
        caretPositionEnd: 0 }),
    setValue: () => undefined,
    onInputChange: () => ({ value: '',
        caretPosition: 0 }),
    onKeyboardInput: () => undefined,
};

const KeyboardStateContext = React.createContext<KeyboardState>(initialState);
const KeyboardDispatchContext = React.createContext<Dispatch<KeyboardActions>>(() => null);

const KeyboardProvider = ({ children }: KeyboardProviderProps) => {
    const [state, dispatch] = useReducer(keyboardReducer, { ...initialState });

    useEffect(() => {
        if (isElectron()) {
            if (state.keyboardOpen) {
                document.body.classList.add(keyboardOpenclass);
            }
            else {
                document.body.classList.remove(keyboardOpenclass);
            }
        }
    }, [state.keyboardOpen]);

    useEffect(() => {
        if (isMobile && isSafari) {
            if (state.inputFocused) {
                document.body.classList.add(focusedClass);
            }
            else {
                document.body.classList.remove(focusedClass);
            }
        }
    }, [state.inputFocused]);

    return (
        <KeyboardStateContext.Provider value={{ ...state }}>
            <KeyboardDispatchContext.Provider value={dispatch}>{children}</KeyboardDispatchContext.Provider>
        </KeyboardStateContext.Provider>
    );
};

const useKeyboardState = () => React.useContext(KeyboardStateContext);
const useKeyboardDispatch = () => React.useContext(KeyboardDispatchContext);

export const useKeyboardIntegration = ({
    fieldValue,
    getValue,
    setValue,
    onInputChange,
    onKeyboardInput,
    keyboardLayout = KeyboardLayouts.Alphanumeric,
    shiftAfterPeriod = false,
    shiftAfterAmpersand = false,
    shiftAfterSpace = false,
    hideKeyboardOnClickOut = false,
    capsLock = false,
}: UseKeyboardIntegrationProps): UseKeyboardIntegrationReturn => {
    const keyboardDispatch = useKeyboardDispatch();

    const getShiftLayout = useCallback((layout: KeyboardLayouts) => {
        const value = fieldValue ?? '';
        const makeCapitalized = value.length === 0 || capsLock;

        switch (layout) {
            case KeyboardLayouts.Alphanumeric:
            case KeyboardLayouts.AlphanumericShift:
                return makeCapitalized ? KeyboardLayouts.AlphanumericShift : KeyboardLayouts.Alphanumeric;
            case KeyboardLayouts.AlphanumericNumpad:
            case KeyboardLayouts.AlphanumericNumpadShift:
                return makeCapitalized ? KeyboardLayouts.AlphanumericNumpadShift : KeyboardLayouts.AlphanumericNumpad;
            case KeyboardLayouts.Email:
            case KeyboardLayouts.EmailShift:
                return capsLock ? KeyboardLayouts.EmailShift : KeyboardLayouts.Email;
            case KeyboardLayouts.Password:
            case KeyboardLayouts.PasswordShift:
                return makeCapitalized ? KeyboardLayouts.PasswordShift : KeyboardLayouts.Password;
            case KeyboardLayouts.PasswordNumpad:
            case KeyboardLayouts.PasswordNumpadShift:
                return makeCapitalized ? KeyboardLayouts.PasswordNumpadShift : KeyboardLayouts.PasswordNumpad;
            default:
                return layout;
        }
    }, [fieldValue, capsLock]);

    const onBlur = useCallback(() => {
        keyboardDispatch({ type: KeyboardDispatchTypes.OnFieldBlurred });
    }, [keyboardDispatch]);

    const onFocus = useCallback((event: React.FocusEvent<Element | Element>) => {
        keyboardDispatch({
            type: KeyboardDispatchTypes.OnFieldFocused,
            payload: {
                inputElement: event.target as HTMLInputElement,
                keyboardLayout: getShiftLayout(keyboardLayout),
                shiftAfterPeriod: shiftAfterPeriod,
                shiftAfterSpace: shiftAfterSpace,
                shiftAfterAmpersand: shiftAfterAmpersand,
                hideKeyboardOnClickOut: hideKeyboardOnClickOut,
                capsLock: capsLock,
                getValue: getValue,
                setValue: setValue,
                onInputChange: onInputChange,
                onKeyboardInput: onKeyboardInput,
            },
        });
    }, [
        keyboardDispatch,
        shiftAfterPeriod,
        shiftAfterSpace,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        getValue,
        setValue,
        onInputChange,
        onKeyboardInput,
        getShiftLayout,
        keyboardLayout,
    ]);

    return {
        onBlur,
        onFocus,
    };
};

export const getShiftLayout: GoMotoFunction<[GetShiftLayoutProps], KeyboardLayouts> = ({ layout, keyboardValue, capsLock }) => {
    const makeCapitalized = (keyboardValue ?? '').length === 0 || capsLock;

    switch (layout) {
        case KeyboardLayouts.Alphanumeric:
        case KeyboardLayouts.AlphanumericShift:
            return makeCapitalized ? KeyboardLayouts.AlphanumericShift : KeyboardLayouts.Alphanumeric;
        case KeyboardLayouts.AlphanumericNumpad:
        case KeyboardLayouts.AlphanumericNumpadShift:
            return makeCapitalized ? KeyboardLayouts.AlphanumericNumpadShift : KeyboardLayouts.AlphanumericNumpad;
        case KeyboardLayouts.Email:
        case KeyboardLayouts.EmailShift:
            return capsLock ? KeyboardLayouts.EmailShift : KeyboardLayouts.Email;
        case KeyboardLayouts.Password:
        case KeyboardLayouts.PasswordShift:
            return makeCapitalized ? KeyboardLayouts.PasswordShift : KeyboardLayouts.Password;
        case KeyboardLayouts.PasswordNumpad:
        case KeyboardLayouts.PasswordNumpadShift:
            return makeCapitalized ? KeyboardLayouts.PasswordNumpadShift : KeyboardLayouts.PasswordNumpad;
        default:
            return layout;
    }
};

const useKeyboardFormField: KeyboardFormFieldFunction = ({
    name,
    value,
    keyboardLayout = KeyboardLayouts.Alphanumeric,
    shiftAfterPeriod = false,
    shiftAfterSpace = false,
    shiftAfterAmpersand = false,
    hideKeyboardOnClickOut = false,
    capsLock = false,
    clearOnBlur,
    setFieldValue,
    onChangeCallback,
    onBlurCallback,
    setNewCaretPosition,
    getNewCaretPosition,
    onInputChangeCallback,
}) => {
    const keyboardDispatch = useKeyboardDispatch();

    const inputRef = useRef<HTMLInputElement>(null);
    const [keyboardValue, setKeyboardValue] = useState<string>(value);
    const [caretPosition, setCaretPosition] = useState<number | null>(null);
    const isFocusedRef = useRef<boolean>(false);

    const getValue: GoMotoFunction<[void], GetValueReturn> = useCallback(() => {
        if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
            const selectionStart: number = getNewCaretPosition ? getNewCaretPosition(inputRef.current.selectionStart, keyboardValue) : inputRef.current.selectionStart;
            const selectionEnd: number = getNewCaretPosition ? getNewCaretPosition(inputRef.current.selectionEnd, keyboardValue) : inputRef.current.selectionEnd;

            return {
                inputValue: keyboardValue,
                caretPositionStart: selectionStart,
                caretPositionEnd: selectionEnd,
            };
        }
        else {
            return {
                inputValue: keyboardValue,
                caretPositionStart: keyboardValue.length,
                caretPositionEnd: keyboardValue.length,
            };
        }
    }, [getNewCaretPosition, keyboardValue]);

    const setValue: GoMotoFunction<[SetValueProps], void> = useCallback(({ value, caretPosition }) => {
        if (isElectron()) {
            if (caretPosition !== null) {
                setCaretPosition(setNewCaretPosition ? setNewCaretPosition(caretPosition, value) : caretPosition);
            }
            else {
                setCaretPosition(value.length);
            }
        }

        setFieldValue(name, value);
        onChangeCallback && onChangeCallback();
    }, [name, onChangeCallback, setFieldValue, setNewCaretPosition]);

    const onInputChange: GoMotoFunction<[OnInputChangeProps], OnInputChangeReturn> = useCallback(({ value, caretPosition, wasBackspace, wasSelectionRange }) => {
        if (onInputChangeCallback) {
            return onInputChangeCallback({
                value: value,
                caretPosition: caretPosition,
                wasBackspace: false,
                wasSelectionRange: false,
            });
        }
        else {
            return {
                value: value,
                caretPosition: caretPosition,
            };
        }
    }, [onInputChangeCallback]);

    const onKeyboardInput: VoidFunction = useCallback(() => {
        if (inputRef.current && !isFocusedRef.current) {
            inputRef.current.focus();
        }
    }, [isFocusedRef]);

    useEffect(() => {
        if (isElectron()) {
            /**
             * Updates the internal ref value when formik's value changes externally.
             */
            setKeyboardValue(value);

            if (isFocusedRef.current) {
                if (inputRef.current) {
                    inputRef.current.setSelectionRange(caretPosition, caretPosition);
                }

                keyboardDispatch({
                    type: KeyboardDispatchTypes.OnFieldRerender,
                    payload: {
                        getValue: getValue,
                        setValue: setValue,
                        onInputChange: onInputChange,
                        onKeyboardInput: onKeyboardInput,
                    },
                });
            }
        }
    }, [
        value,
        getValue,
        keyboardValue,
        setValue,
        name,
        onChangeCallback,
        setFieldValue,
        onKeyboardInput,
        isFocusedRef,
        caretPosition,
        keyboardDispatch,
        onInputChange,
    ]);

    const onFocus: GoMotoFunction<[FocusEvent<Element>], void> = (event) => {
        if (isElectron()) {
            keyboardDispatch({
                type: KeyboardDispatchTypes.OnFieldFocused,
                payload: {
                    inputElement: event.target as HTMLInputElement,
                    keyboardLayout: getShiftLayout({
                        layout: keyboardLayout,
                        keyboardValue: value,
                        capsLock,
                    }),
                    shiftAfterPeriod: shiftAfterPeriod,
                    shiftAfterSpace: shiftAfterSpace,
                    shiftAfterAmpersand: shiftAfterAmpersand,
                    hideKeyboardOnClickOut: hideKeyboardOnClickOut,
                    capsLock: capsLock,
                    getValue: getValue,
                    setValue: setValue,
                    onInputChange: onInputChange,
                    onKeyboardInput: onKeyboardInput,
                },
            });
        }

        isFocusedRef.current = true;
    };

    const onBlur: VoidFunction = () => {
        if (isElectron()) {
            keyboardDispatch({ type: KeyboardDispatchTypes.OnFieldBlurred });
        }

        onBlurCallback && onBlurCallback();

        isFocusedRef.current = false;
    };

    return {
        inputRef,
        keyboardValue,
        setKeyboardValue,
        caretPosition,
        setCaretPosition,
        isFocused: isFocusedRef.current,
        onFocus,
        onChange: setValue,
        onBlur,
    };
};


export { KeyboardProvider, useKeyboardDispatch, useKeyboardState, KeyboardDispatchTypes, useKeyboardFormField };
